
import { useState, useEffect } from 'react';

export interface ToastItem {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

let toastListeners: ((toast: ToastItem) => void)[] = [];
let toastId = 0;

/**
 * Shows a toast notification.
 * @param message The message to display.
 * @param type The type of toast (defaults to 'success').
 */
export function showToast(
  message: string,
  type: 'success' | 'error' | 'info' = 'success'
) {
  const toast: ToastItem = { id: ++toastId, message, type };
  toastListeners.forEach((listener) => listener(toast));
}

/**
 * Hook that provides the current list of toasts.
 * Handles subscription / un‑subscription safely.
 */
export function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    const listener = (toast: ToastItem) => {
      // Add the new toast
      setToasts((prev) => [...prev, toast]);

      // Auto‑remove after 3 seconds
      const timer = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
      }, 3000);

      // Cleanup the timer if the component unmounts early
      return () => clearTimeout(timer);
    };

    toastListeners.push(listener);

    // Clean‑up: remove listener when the hook is unmounted
    return () => {
      toastListeners = toastListeners.filter((l) => l !== listener);
    };
  }, []);

  return toasts;
}

/**
 * Container component that renders all active toasts.
 * Includes minimal inline CSS for the slide‑in animation.
 */
export default function ToastContainer() {
  const toasts = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg border animate-[slideInRight_0.3s_ease-out] pointer-events-auto ${
            toast.type === 'success'
              ? 'bg-[#0a0a0a] border-[#00ff41] text-[#00ff41]'
              : toast.type === 'error'
              ? 'bg-[#0a0a0a] border-red-500 text-red-400'
              : 'bg-[#0a0a0a] border-[#00ff41]/50 text-[#00ff41]/80'
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i
              className={`text-lg ${
                toast.type === 'success'
                  ? 'ri-check-line'
                  : toast.type === 'error'
                  ? 'ri-error-warning-line'
                  : 'ri-information-line'
              }`}
            />
          </div>
          <span className="text-sm font-medium">{toast.message}</span>
        </div>
      ))}

      {/* Inline CSS for the slide‑in animation */}
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
